import {$inject, $type} from "../utils/di-container.js";
import {LoggerService} from "./logger.service.js";
import {Badge} from "./badge.js";
import {parseDate} from "../utils/date-utils.js";
import {appContext} from "../badge2.context.js";
import {PlaceholderService} from "./placeholder.service.js";
import {extractNormalUrlParts} from "../utils/url-utils.js";
import {defaultLanguage} from "./i18n.service.js";
import { FeatureService } from './feature.service.js';
import { deepClone } from '../../test/util/deep-clone.mjs';

const defaults = {
  targetElementType: 'div',
  platform: 'desktop',
  variant: 'mid',
  language: defaultLanguage,
  plan: 'free',
  zIndex: 2000,
  verticalLocation: {
    desktop: 'middle',
    mobile: 'bottom',
  },
  horizontalLocation: {
    desktop: 'right',
    mobile: 'right',
  }
};

const invalidLanguageValues = [
  '',
  null,
  undefined,
  'null',
  'undefined',
  'Insert_language_here'
];

/**
 * Once initialized with some badge subscription information, defines possible badges and then
 *   when a placeholder is given, it matches with the information and activates the badge (if the case).
 * The service deals with all badges as a group, while delegating individually to {@link Badge}.
 */
export class BadgeService {

  /**
   * @type {LoggerService}
   */
  _logger;

  /**
   * @type {Badge[]}
   * Current badges
   */
  _badges = [];

  /**
   * @type {PlaceholderService}
   */
  _placeholderService;

  /**
   * @type {FeatureService}
   */
  _featureService;

  /**
   * List of global special rules to apply
   * @type {Object<string, boolean>}
   */
  specialRules = {};

  [$inject](obtain) {
    this._logger = obtain(LoggerService);
    this._placeholderService = obtain(PlaceholderService);
    this._featureService = obtain(FeatureService);
  }

  /**
   * @param {_BadgeMeta} badgeMeta
   * @param {_ApiResponse_fetchBadgeInfo} badgeInfo
   * @param {_BadgePlatform} platform
   * @returns {void}
   */
  initialize(badgeMeta, badgeInfo, platform) {
    this._logger.info('badges/initializing', `Initializing all badges`, () => ({ badgeMeta: deepClone(badgeMeta), badgeInfo: deepClone(badgeInfo), platform }));
    this._logger.debug('metadata/values', `Metadata values: ${JSON.stringify(badgeMeta)}`);

    if (this._badges && this._badges.length > 0) {
      this._badges = [];
    }

    if (badgeInfo) {
      if (badgeInfo.error) {
        this._logger.error('global/error-data', `Badge info error: ${badgeInfo.error.type} | ${badgeInfo.error.code} | ${badgeInfo.error.details}`, () => ({ badgeMeta: deepClone(badgeMeta), error: badgeInfo.error }));
        return;
      }

      let product = badgeInfo.product;
      if (product && product.error) {
        this._logger.warn('badge/fallback/product-data-error', `Product data error: ${JSON.stringify(product.error)}. Product badges will fallback to shop.`, () => ({ badgeMeta: deepClone(badgeMeta), error: product.error }));
        product = null;
      }

      (badgeInfo[`${platform}Configuration`] || []).forEach(/** @type {_ApiResponse_fetchBadgeInfo_BadgeConfig} */badgeConfig => {
        /** @type {_BadgeType} */
        const type = {
          t0: badgeInfo.t0,
          platform: platform || defaults.platform,
          language: invalidLanguageValues.includes(badgeMeta.language) ? defaults.language : (badgeMeta.language || defaults.language),
          environment: badgeMeta.environment || 'live',
          variant: badgeConfig?.type?.toLowerCase() || defaults.variant,
          plan: badgeInfo?.subscription?.toLowerCase() || defaults.plan,
          options: {
            targetElementType: defaults.targetElementType,
            enabled: badgeConfig.enabled,
            zIndex: (badgeConfig.zIndex === null) ? defaults.zIndex : badgeConfig.zIndex,
            position: {
              vertical: {
                location: badgeConfig.position || defaults.verticalLocation[platform],
                settings: {
                  distance: (badgeConfig.positionSettings || {}).distance,
                  reference: (badgeConfig.positionSettings || {}).direction,
                  unit: (badgeConfig.positionSettings || {}).unit
                },
              },
              horizontal: {
                location: badgeConfig.horizontalPosition || defaults.horizontalLocation[platform],
                settings: {
                  distance: (badgeConfig.horizontalPositionSettings || {}).distance,
                  reference: (badgeConfig.horizontalPositionSettings || {}).direction,
                  unit: (badgeConfig.horizontalPositionSettings || {}).unit
                }
              }
            }
          }
        };

        const company = Object.assign({}, badgeInfo.retailerInfo);
        company.lastVerifiedDate = badgeInfo.responseDate;
        company.tuvApprovalDate = parseDate(badgeInfo.tuvApprovalDate, 'dd.mm.yyyy');
        company.homepageBare = extractNormalUrlParts(company.homepage, {noWww: true}).host;

        const brands = badgeInfo.brands;

        let getDisplayName = brand => brand.alias || brand.name;

        if (brands) {
          brands.forEach(brand => brand.displayName = getDisplayName(brand));
        }
        if (product && product.brand) {
          product.brand.displayName = getDisplayName(product.brand);
        }

        const badge = new Badge(type, company, brands, product);
        appContext.applyInject(badge);
        this._badges.push(badge);
      });
      let globalConfiguration = badgeInfo.globalConfiguration;
      this.specialRules = {
        hideSidebarWhenContentPresent: globalConfiguration ? globalConfiguration.hideSidebarWhenContentPresent : false
      };
    } else {
      this._logger.error('global/missing-data', 'Cannot initialize badges - missing badgeInfo.', () => ({ badgeMeta: deepClone(badgeMeta) }));
    }
  }

  startHandlingLocationChanges() {
    // handle location change
    if (!this._interval) {
      // only reliable approach to determine soft location changes (e.g. caused history.push events)
      let lastLocation = window.location.href;
      this._interval = setInterval(() => {
        if (lastLocation !== window.location.href) {
          this._logger.debug('global/page-url-change', `Location changed: ${window.location.href}. Badges will be refreshed.`, () => ({ lastLocation, currentLocation: window.location.href }));
          this.refreshAll('page-url-change');
          lastLocation = window.location.href;
        }
      }, 1000);
    }
  }

  stopHandlingLocationChanges() {
    clearInterval(this._interval);
  }

  /**
   * @param {_Placeholder} placeholder
   * @return {Badge|null} the activated badge, if any
   */
  checkPlaceholder(placeholder) {
    this._logger.debug('placeholder/found', `New placeholder found. Checking badge: ${placeholder.variant}`, () => ({ variant: placeholder.variant }));

    const badge = this.getBadge({ variant: placeholder.variant });

    if (badge) {
      let replace;
      if (badge.isActive() || badge.activating) {
        replace = true;
      }
      if (replace) {
        this._logger.debug('placeholder/replace-badge', `Replacing already active badge: ${badge.toString()}`, () => ({ variant: placeholder.variant, badge: badge.toInfo() }));
        badge.deactivate('replace');
      }
      badge.placeholder = placeholder;
      badge.activate('placeholder-found');
    } else {
      this._logger.warn('placeholder/badge-not-enabled', `Placeholder found but badge is not enabled: ${placeholder.variant}`, () => ({ variant: placeholder.variant }));
    }

    return badge;
  }

  /**
   * @param {_Placeholder} placeholder
   * @return {Badge|null} the de-activated badge, if any
   */
  deactivateBadgeFor(placeholder) {
    this._logger.debug('placeholder/removed', `Placeholder removed: ${placeholder.variant}`, () => ({ variant: placeholder.variant }));
    const badge = this._badges.find(badge => badge.placeholder === placeholder);
    if (badge) {
      badge.deactivate('placeholder-removed');
      return badge;
    }
  }

  /**
   * @param {_BadgePlatform} platform
   */
  changePlatform(platform) {
    // update existing badges
    this._badges.forEach(badge => badge.changePlatform(platform));
  }

  /**
   * @param {_BadgeVariant} [variant]
   * @param {boolean} [activeOnly]
   */
  getBadges({ activeOnly, variant }) {
    return this._badges
      .filter(badge => !activeOnly || badge.isActive())
      .filter(badge => !variant || (badge.type.variant === variant));
  }

  /**
   * @param {_BadgeVariant} variant
   * @param {boolean} [activeOnly]
   */
  getBadge({ activeOnly, variant }) {
    return this.getBadges({ activeOnly, variant })[0];
  }

  refreshAll(reason) {
    this.getBadges({}).map(badge => badge.refresh(reason));
  }

  deactivateAll(reason) {
    this.getBadges({}).forEach(badge => badge.deactivate(reason));
  }

  notifyImageError(imageUrl) {
    if (this._badges.some(b => b.isActiveOrActivating())) {
      this._logger.error('global/image-load-error', `Image loading error detected. Disabling all badges to prevent visual glitches: ${imageUrl}`, () => ({ imageUrl }))
      this.deactivateAll('image-loading-error');
    }
  }

}

BadgeService[$type] = Symbol('BadgeService');
